<template>

  <form class="form" novalidate @submit.prevent="handleSubmit">
    <FormRow :withThreeColumns="true">
      <FormInput :ref="addFormInputRef"
                 v-model="contact.firstName"
                 :label="t('global.firstName')"
                 name="firstName"
                 type="text"
                 @validated="handleValidation"/>
      <FormInput :ref="addFormInputRef"
                 v-model="contact.lastName"
                 :label="t('global.lastName')"
                 name="lastName"
                 type="text"
                 @validated="handleValidation"/>
      <FormInput :ref="addFormInputRef"
                 v-model="contact.jobTitle"
                 :label="t('global.jobTitle')"
                 :rules="[]"
                 name="jobTitle"
                 type="text"
                 @validated="handleValidation"/>
    </FormRow>
    <FormRow :withThreeColumns="true">
      <FormInput :ref="addFormInputRef"
                 v-model="contact.apartment"
                 :label="t('global.apartment')"
                 :rules="[]"
                 name="apartment"
                 type="text"
                 @validated="handleValidation"/>
      <FormInput :ref="addFormInputRef"
                 v-model="contact.street"
                 :label="t('global.street')"
                 :list="'address-list'"
                 name="street"
                 type="text"
                 @validated="handleValidation"
                 @update:modelValue="handleInput">
        <template v-slot:before-input>
          <datalist id="address-list">
            <option v-for="address in addressOptions" :id="address.id" :key="address.id">
              {{ address.text }}
            </option>
          </datalist>
        </template>
      </FormInput>
      <FormInput :ref="addFormInputRef"
                 v-model="contact.city"
                 :label="t('global.city')"
                 name="city"
                 type="text"
                 @validated="handleValidation"/>
    </FormRow>
    <FormRow :withThreeColumns="true">
      <FormInput :ref="addFormInputRef"
                 v-model="contact.zipCode"
                 :label="t('global.zipCode')"
                 :rules="[required, mustMatchZipCodeFormat]"
                 name="zipCode"
                 type="zip"
                 @validated="handleValidation"/>
      <div class="form__duo-fields">
        <FormInput :ref="addFormInputRef"
                   v-model="contact.phoneNumber"
                   :label="t('global.phoneNumber')"
                   :rules="[required, mustMatchPhoneNumberFormat]"
                   name="phoneNumber"
                   type="tel"
                   @validated="handleValidation"/>
        <FormInput :ref="addFormInputRef"
                   v-model="contact.phoneExtension"
                   :label="t('global.phoneExtension')"
                   :rules="[]"
                   name="phoneExtension"
                   type="number"
                   @validated="handleValidation"/>
      </div>
      <FormInput :ref="addFormInputRef"
                 v-model="contact.email"
                 :label="t('global.email')"
                 name="email"
                 type="text"
                 @validated="handleValidation"/>
    </FormRow>
    <button class="form__submit btn btn--fullscreen">{{ t('global.save') }}</button>
  </form>
</template>

<script lang="ts" setup>
import {ref} from "vue"
import {useI18n} from "vue3-i18n"
import {notifyError} from "@/notify"
import {mustMatchPhoneNumberFormat, mustMatchZipCodeFormat, required} from "@/validation/rules"
import {Status} from "@/validation"
import {Contact} from "@/types/entities/contact"
import FormRow from "@/components/forms/FormRow.vue"
import FormInput from "@/components/forms/FormInput.vue"
import {IFilterAddressesRequest, IGetAddressRequest} from "@/types/requests";
import {IAddress} from "@/types/responses";
import {useAddressService} from "@/inversify.config";

// eslint-disable-next-line no-undef
const props = defineProps<{
  contact?: Contact
}>()

// eslint-disable-next-line
const emit = defineEmits<{
  (event: "formSubmit", contact: Contact): void
}>()

const {t, getLocale} = useI18n()
const addressService = useAddressService()

const addressOptions = ref<IAddress[]>([])
const contact = ref<Contact>(props.contact ?? {})

const formInputs = ref<(typeof FormInput)[]>([])
const inputValidationStatuses: any = {}

function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref))
    formInputs.value.push(ref)
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid
}

async function handleSubmit() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())
  if (Object.values(inputValidationStatuses).some(x => x === false)) {
    notifyError(t('global.formErrorNotification'))
    return
  }
  emit("formSubmit", contact.value)
}

async function handleInput(inputValue: string) {
  let selectedOptions = addressOptions.value.filter(x => x.text == inputValue)
  if (selectedOptions.length > 0) {
    let request = {
      id: selectedOptions[0].id,
      language: getLocale()
    } as IGetAddressRequest
    let response = await addressService.getAddress(request)
    contact.value.street = response.detailedAddress.street
    contact.value.city = response.detailedAddress.city
    contact.value.zipCode = response.detailedAddress.zipCode.replace(' ', '')
    return;
  }
  await loadMatchingAddresses(inputValue)
}

async function loadMatchingAddresses(inputValue: string) {
  let request = {
    street: inputValue,
    language: getLocale()
  } as IFilterAddressesRequest
  let addresses = await addressService.filterAddresses(request)
  addressOptions.value = addresses.matchingAddresses
}
</script>
